import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Header,
  Input,
  SpaceBetween,
  Table,
} from "@cloudscape-design/components";
import { payloadFromToken } from "../api/auth";
import { getPhoneDirectory, updatePhoneDirectory } from "../api/api";
import { PRIVILEGED_USERS } from "../constants/auth";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createEntry = (entry = {}) => ({
  id: entry.id || `${Date.now()}-${Math.random()}`,
  extension: entry.extension ?? entry.Extension ?? "",
  FirstName: entry.FirstName ?? entry.firstName ?? "",
  LastName: entry.LastName ?? entry.lastName ?? "",
  Email: entry.Email ?? entry.email ?? "",
  Site: entry.Site ?? entry.site ?? "",
});

const normalizeEntry = (entry) => ({
  id: entry.id,
  extension: entry.extension?.trim() ?? "",
  FirstName: entry.FirstName?.trim() ?? "",
  LastName: entry.LastName?.trim() ?? "",
  Email: entry.Email?.trim() ?? "",
  Site: entry.Site?.trim() ?? "",
});

const formatLastUpdated = (value) => {
  if (!value || value === "0") return "Not updated yet";
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return "Unknown";
  return new Date(timestamp).toLocaleString();
};

const directoryToEntries = (directory = {}) =>
  Object.entries(directory)
    .map(([extension, entry]) =>
      createEntry({
        extension,
        ...entry,
      })
    )
    .sort((a, b) => Number(a.extension) - Number(b.extension));

const entriesToDirectory = (normalizedEntries) =>
  normalizedEntries.reduce((acc, entry) => {
    acc[entry.extension] = {
      FirstName: entry.FirstName,
      LastName: entry.LastName,
      Email: entry.Email,
      Site: entry.Site,
    };
    return acc;
  }, {});

function PhoneDirectoryAdmin({ setAlert }) {
  const [username, setUsername] = React.useState("");
  const [entries, setEntries] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState("0");

  React.useEffect(() => {
    const storedToken =
      window.localStorage.getItem("id_token") ||
      window.localStorage.getItem("access_token");

    if (!storedToken) return;

    try {
      const payload = payloadFromToken(storedToken);
      const derivedUsername =
        payload?.["cognito:username"] || payload?.username || payload?.email;
      if (derivedUsername) {
        setUsername(derivedUsername);
      }
    } catch (err) {
      console.debug("Unable to derive username from token", err);
    }
  }, []);

  const isPrivileged = PRIVILEGED_USERS.includes(username);

  const loadDirectory = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPhoneDirectory();
      const directory =
        data?.directory && typeof data.directory === "object"
          ? data.directory
          : data;
      const updatedAt = data?.lastUpdated ?? "0";
      setEntries(directoryToEntries(directory));
      setLastUpdated(String(updatedAt));
    } catch (err) {
      console.error(err);
      setAlert({
        heading: "Something went wrong",
        variant: "danger",
        text: err?.message || "Unable to load the phone directory.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [setAlert]);

  React.useEffect(() => {
    let isMounted = true;

    if (!isPrivileged) {
      setIsLoading(false);
      return undefined;
    }

    const guardedLoad = async () => {
      if (!isMounted) return;
      await loadDirectory();
    };

    guardedLoad();

    return () => {
      isMounted = false;
    };
  }, [isPrivileged, loadDirectory]);

  const handleFieldChange = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleAddRow = () => {
    setEntries((prev) => [...prev, createEntry()]);
  };

  const handleDeleteRow = (id) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const validateEntries = () => {
    const normalized = entries.map(normalizeEntry);
    const errors = [];
    const extensionCounts = normalized.reduce((acc, entry) => {
      const key = entry.extension;
      if (key) acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    normalized.forEach((entry, index) => {
      const rowLabel = `Row ${index + 1}`;
      if (!entry.extension) {
        errors.push(`${rowLabel}: Extension is required.`);
      } else if (!/^\d+$/.test(entry.extension)) {
        errors.push(`${rowLabel}: Extension must be numeric.`);
      } else if (extensionCounts[entry.extension] > 1) {
        errors.push(`${rowLabel}: Extension must be unique.`);
      }

      if (!entry.FirstName) errors.push(`${rowLabel}: First name is required.`);
      if (!entry.LastName) errors.push(`${rowLabel}: Last name is required.`);
      if (!entry.Email) {
        errors.push(`${rowLabel}: Email is required.`);
      } else if (!emailRegex.test(entry.Email)) {
        errors.push(`${rowLabel}: Email must be valid.`);
      }
      if (!entry.Site) errors.push(`${rowLabel}: Site is required.`);
    });

    if (errors.length > 0) {
      setAlert({
        heading: "Validation error",
        variant: "danger",
        text: errors.join(" "),
      });
      return null;
    }

    return normalized;
  };

  const handleSave = async () => {
    if (isSaving) return;
    const normalized = validateEntries();
    if (!normalized) return;

    setIsSaving(true);
    try {
      const payload = entriesToDirectory(
        normalized.map(({ id, ...rest }) => rest)
      );
      const response = await updatePhoneDirectory(payload, lastUpdated);
      setEntries(normalized);
      if (response?.lastUpdated) {
        setLastUpdated(String(response.lastUpdated));
      }
      setAlert({
        heading: "Phone directory updated",
        variant: "success",
        text: "Your changes have been saved.",
      });
    } catch (err) {
      console.error(err);
      if (err?.status === 409) {
        await loadDirectory();
        setAlert({
          heading: "Changes out of date",
          variant: "warning",
          text: "The phone directory was updated elsewhere. The latest data has been reloaded.",
        });
        return;
      }
      setAlert({
        heading: "Something went wrong",
        variant: "danger",
        text: err?.message || "Unable to save phone directory changes.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const tableItems = React.useMemo(
    () =>
      entries.map((entry, index) => ({
        ...entry,
        rowNumber: index + 1,
      })),
    [entries]
  );

  const columnDefinitions = [
    {
      id: "rowNumber",
      header: "#",
      cell: (item) => item.rowNumber,
    },
    {
      id: "extension",
      header: "Extension",
      cell: (item) => (
        <Input
          value={item.extension}
          onChange={({ detail }) =>
            handleFieldChange(item.id, "extension", detail.value)
          }
        />
      ),
    },
    {
      id: "firstName",
      header: "First Name",
      cell: (item) => (
        <Input
          value={item.FirstName}
          onChange={({ detail }) =>
            handleFieldChange(item.id, "FirstName", detail.value)
          }
        />
      ),
    },
    {
      id: "lastName",
      header: "Last Name",
      cell: (item) => (
        <Input
          value={item.LastName}
          onChange={({ detail }) =>
            handleFieldChange(item.id, "LastName", detail.value)
          }
        />
      ),
    },
    {
      id: "email",
      header: "Email",
      cell: (item) => (
        <Input
          value={item.Email}
          onChange={({ detail }) =>
            handleFieldChange(item.id, "Email", detail.value)
          }
        />
      ),
    },
    {
      id: "site",
      header: "Site",
      cell: (item) => (
        <Input
          value={item.Site}
          onChange={({ detail }) =>
            handleFieldChange(item.id, "Site", detail.value)
          }
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (item) => (
        <Button
          onClick={() => handleDeleteRow(item.id)}
          variant="link"
        >
          Delete
        </Button>
      ),
    },
  ];

  if (!isPrivileged) {
    return (
      <ContentLayout header={<Header variant="h1">Phone Directory</Header>}>
        <Container>
          <Alert
            statusIconAriaLabel="Info"
            header="Access restricted"
            variant="info"
          >
            You do not have permission to manage the phone directory.
          </Alert>
        </Container>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Box fontSize="body-s" color="text-body-secondary">
                Last updated: {formatLastUpdated(lastUpdated)}
              </Box>
              <Button onClick={handleAddRow}>Add entry</Button>
              <Button
                onClick={handleSave}
                variant="primary"
                loading={isSaving}
              >
                Save changes
              </Button>
            </SpaceBetween>
          }
        >
          Phone Directory
        </Header>
      }
    >
      <Container>
        <Table
          trackBy="id"
          columnDefinitions={columnDefinitions}
          items={tableItems}
          loading={isLoading}
          loadingText="Loading phone directory"
          empty={
            <Box textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <Box variant="strong">No entries</Box>
                <Button onClick={handleAddRow}>Add entry</Button>
              </SpaceBetween>
            </Box>
          }
        />
      </Container>
    </ContentLayout>
  );
}

export default PhoneDirectoryAdmin;
